import {Hsva, Rgba, hsvaToRgba, parseColor, rgbaToHsva, sameColor, toHex8String, toHexString, toRgbString} from './color-format';

/** What a colour picker holds between openings, as Spectrum kept it: the panel's position and whether it is empty. */
export interface ColorPanelState {
  hsva: Hsva;
  empty: boolean;
}

/** How the panel's text field and a picker's input show a colour. */
export type ColorPanelFormat = 'hex' | 'rgb';

export const EMPTY_COLOR_STATE: ColorPanelState = {hsva: {h: 0, s: 0, v: 0, a: 1}, empty: true};

/** The colour of a state; null when it is empty and emptying is allowed. */
export function stateColor(state: ColorPanelState, allowEmpty = true): Rgba | null {
  return allowEmpty && state.empty ? null : hsvaToRgba(state.hsva);
}

/**
 * Spectrum's `set`: an empty colour empties the state; any text, even text no format reads, moves it (unreadable
 * text gives black, as TinyColor made it). The same colour leaves the state as it is.
 */
export function setStateColor(state: ColorPanelState, color: Rgba | string | null, allowEmpty = true): ColorPanelState {
  const next = typeof color === 'string' ? (color ? parseColor(color) : null) : color;
  if (sameColor(next, stateColor(state, allowEmpty))) {
    return state;
  }
  if (!next && allowEmpty) {
    return {...state, empty: true};
  }
  return {hsva: rgbaToHsva(next || parseColor('')), empty: false};
}

/** The text a picker shows for a colour: rgb when asked or when the colour is see-through, else hex. */
export function displayColor(color: Rgba | null, format: ColorPanelFormat): string {
  if (!color) {
    return '';
  }
  return format === 'rgb' || color.a < 1 ? toRgbString(color) : toHexString(color);
}

/** The value a picker stores: rgb strings with `alpha`, hex with alpha as `#rrggbbaa` with `showAlpha`, else hex. */
export function storedColor(color: Rgba | null, options: {alpha?: boolean; showAlpha?: boolean}): string {
  if (!color) {
    return '';
  }
  if (options.alpha) {
    return toRgbString(color);
  }
  return options.showAlpha && color.a < 1 ? toHex8String(color) : toHexString(color);
}

/** Spectrum's recent row: colours outside the palette, oldest first, up to 8, kept per picker. */
export function addRecentColor(recent: string[], color: Rgba | null, palette: ReadonlySet<string>): string[] {
  if (!color) {
    return recent;
  }
  const rgb = toRgbString(color);
  if (palette.has(rgb) || recent.includes(rgb)) {
    return recent;
  }
  return [...recent, rgb].slice(-8);
}
